import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  client,
  DeviceSummaryDto,
  DevicesControllerListSummariesResponse,
} from "@/client";
import { compareSemVer, isDefined } from "@/lib/utils";
import type { AgentDowngradeTarget } from "./agent-downgrade-warning";

// Past this many Windows devices on the selected sites the targets count as
// unknown.
const SITE_DEVICES_LIMIT = 1000;

// The targets an agent version would downgrade: those running a newer agent,
// and those whose running agent is unknown. undefined when the targets
// themselves are unknown, which must be warned about too.
export function findDowngradedTargets(
  version: string,
  targets: AgentDowngradeTarget[] | undefined,
): AgentDowngradeTarget[] | undefined {
  return targets?.filter(
    (target) =>
      !target.agentVersion || compareSemVer(version, target.agentVersion) === 1,
  );
}

// Only Windows agents break on a downgrade.
export const deviceToAgentTarget = (
  device: DeviceSummaryDto,
): AgentDowngradeTarget | undefined =>
  device.characteristics?.os === "linux"
    ? undefined
    : { name: device.name, agentVersion: device.agentVersion };

// The sites' devices as targets, or undefined while they are not all fetched.
export function siteDevicesToAgentTargets(
  page: DevicesControllerListSummariesResponse | undefined,
): AgentDowngradeTarget[] | undefined {
  if (!page || (page.results?.length ?? 0) < (page.total ?? 0))
    return undefined;
  return (page.results ?? []).map(deviceToAgentTarget).filter(isDefined);
}

// The selected rows of a bulk action's table, or undefined when some selected
// rows are not loaded (e.g. select all over more rows than fetched).
export function useSelectedRows<TRow extends { id: number }>(
  context: any,
  count: number,
): TRow[] | undefined {
  const queryResult = context.useTableQueryResult();
  const [rowSelection] = context.useTableRowSelection();
  const [allSelected] = context.useTableAllSelected();
  return useMemo(() => {
    const rows: TRow[] = queryResult.data?.results ?? [];
    const selected = allSelected
      ? rows
      : rows.filter((row) => rowSelection[`${row.id}`]);
    return selected.length < count ? undefined : selected;
  }, [queryResult.data, rowSelection, allSelected, count]);
}

// A device's own reported agent version, not its deployment history.
export function useDeviceAgentTargets(
  deviceId: number | undefined,
): AgentDowngradeTarget[] | undefined {
  const { data } = useQuery({
    ...client.devices.listSummaries.asQueryOptions({
      deviceIds: [deviceId!],
      page: 1,
      limit: 1,
    }),
    enabled: isDefined(deviceId),
  });
  return useMemo(() => {
    const device = data?.results?.[0];
    if (!device) return undefined;
    return [deviceToAgentTarget(device)].filter(isDefined);
  }, [data]);
}

// The agents the sites' Windows devices run; a site's own agent version is
// only its deployment history and may not match them.
export function useSiteAgentTargets(
  siteIds: number[] | undefined,
): AgentDowngradeTarget[] | undefined {
  // An empty siteIds filter would match every device of the account.
  const hasSites = !!siteIds && siteIds.length > 0;
  const { data } = useQuery({
    ...client.devices.listSummaries.asQueryOptions({
      siteIds,
      os: "windows",
      page: 1,
      limit: SITE_DEVICES_LIMIT,
    }),
    enabled: hasSites,
  });
  return useMemo(() => {
    if (!siteIds) return undefined;
    if (!hasSites) return [];
    return siteDevicesToAgentTargets(data);
  }, [siteIds, hasSites, data]);
}

// Two downgrade risks are the same when the same targets run the same agents.
export function sameDowngradeTargets(
  a: AgentDowngradeTarget[] | undefined,
  b: AgentDowngradeTarget[] | undefined,
): boolean {
  if (!a || !b) return a === b;
  const key = (targets: AgentDowngradeTarget[]) =>
    targets
      .map((target) => `${target.name}\u0000${target.agentVersion ?? ""}`)
      .sort()
      .join("\u0001");
  return key(a) === key(b);
}

export type AgentDowngradeConfirmation = {
  // The risk shown on the warning step, frozen when version selection is
  // left; null when no warning step is shown.
  warning: { targets?: AgentDowngradeTarget[] } | null;
  acknowledged: boolean;
  setAcknowledged: (acknowledged: boolean) => void;
  // Freezes the current risk as the warning to show.
  capture: () => void;
  // Whether a deployment may be created: no current risk, or the current
  // risk is the one that was acknowledged.
  isConfirmed: boolean;
};

// The target lookups can resolve or refetch after version selection, so the
// warning step is decided when version selection is left rather than live,
// keeping later steps in place, and is checked again before deploying.
export function useAgentDowngradeConfirmation(
  downgradedTargets: AgentDowngradeTarget[] | undefined,
  resetKey: unknown,
): AgentDowngradeConfirmation {
  const [warning, setWarning] =
    useState<AgentDowngradeConfirmation["warning"]>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const isDowngrade = downgradedTargets?.length !== 0;

  useEffect(() => {
    setWarning(null);
    setAcknowledged(false);
  }, [resetKey]);

  const capture = useCallback(() => {
    const next = isDowngrade ? { targets: downgradedTargets } : null;
    const same =
      next === null
        ? warning === null
        : warning !== null &&
          sameDowngradeTargets(warning.targets, next.targets);
    if (same) return;
    setWarning(next);
    setAcknowledged(false);
  }, [isDowngrade, downgradedTargets, warning]);

  const isConfirmed =
    !isDowngrade ||
    (warning !== null &&
      acknowledged &&
      sameDowngradeTargets(warning.targets, downgradedTargets));

  return { warning, acknowledged, setAcknowledged, capture, isConfirmed };
}
